import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useInfiniteQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query'
import { directMessageApi } from '@/services/api/directMessages'
import { formatFullDate } from '@/utils/chatHelpers'
import { DmMessageBubble } from './DmMessageBubble'
import type { DmConversation, DmCursor, DmMessage, DmMessagePage } from '@/types/direct-message'

interface DmMessageListProps {
  conversation: DmConversation
  currentUserId: string | undefined
}

const MESSAGES_PER_PAGE = 50

function groupByDate(messages: DmMessage[]): Record<string, DmMessage[]> {
  const groups: Record<string, DmMessage[]> = {}
  for (const m of messages) {
    const dateKey = m.created_at.split('T')[0] || 'unknown'
    if (!groups[dateKey]) groups[dateKey] = []
    groups[dateKey].push(m)
  }
  return groups
}

export function DmMessageList({ conversation, currentUserId }: DmMessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null)
  const isInitialLoad = useRef(true)
  const queryClient = useQueryClient()
  const queryKey = ['dm-messages', conversation.conversation_id] as const

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      directMessageApi.getMessages(
        conversation.conversation_id,
        MESSAGES_PER_PAGE,
        pageParam as DmCursor | undefined,
      ),
    getNextPageParam: (lastPage) => {
      if (!lastPage.has_more || lastPage.messages.length === 0) return undefined
      const oldest = lastPage.messages[0]
      if (!oldest) return undefined
      return { before: oldest.created_at, before_id: oldest.id }
    },
    initialPageParam: undefined as DmCursor | undefined,
  })

  useEffect(() => {
    const conversationId = conversation.conversation_id
    const key = ['dm-messages', conversationId] as const
    const mergeLatest = (latest: DmMessagePage) => {
      queryClient.setQueryData<InfiniteData<DmMessagePage, DmCursor | undefined>>(
        key,
        (old) => {
          if (!old?.pages?.length) return old
          const existing = new Set(old.pages.flatMap(p => p.messages.map(m => m.id)))
          const newcomers = latest.messages.filter(m => !existing.has(m.id))
          if (newcomers.length === 0) return old
          const pages = [...old.pages]
          const first = pages[0]
          if (!first) return old
          pages[0] = { ...first, messages: [...first.messages, ...newcomers] }
          return { ...old, pages }
        },
      )
    }
    const poll = () => {
      if (document.hidden) return
      void directMessageApi.getMessages(conversationId, MESSAGES_PER_PAGE).then(mergeLatest).catch(() => {})
    }
    const id = window.setInterval(poll, 10000)
    const onVis = () => { if (!document.hidden) poll() }
    document.addEventListener('visibilitychange', onVis)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [conversation.conversation_id, queryClient])

  const messages = useMemo(() => {
    const seen = new Set<string>()
    const out: DmMessage[] = []
    const ordered = [...(data?.pages ?? [])].reverse().flatMap(p => p.messages)
    for (const m of ordered) {
      if (seen.has(m.id)) continue
      seen.add(m.id)
      out.push(m)
    }
    return out
  }, [data])

  const lastId = messages[messages.length - 1]?.id

  useEffect(() => {
    if (!lastId) return
    messagesEndRef.current?.scrollIntoView({
      behavior: isInitialLoad.current ? 'auto' : 'smooth',
    })
    isInitialLoad.current = false
  }, [lastId])

  useEffect(() => {
    isInitialLoad.current = true
  }, [conversation.conversation_id])

  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    const entry = entries[0]
    if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  useEffect(() => {
    const trigger = loadMoreTriggerRef.current
    if (!trigger) return
    const observer = new IntersectionObserver(handleIntersection, { threshold: 0.1 })
    observer.observe(trigger)
    return () => observer.disconnect()
  }, [handleIntersection])

  const grouped = groupByDate(messages)
  const participantMap = new Map(conversation.participants.map(p => [p.user_id, p]))

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-gray-50" role="log" aria-label="Nachrichtenverlauf">
      <div ref={loadMoreTriggerRef} className="h-1" />
      {hasNextPage && (
        <div className="text-center py-2">
          <button
            type="button"
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
            className="text-xs text-blue-600 hover:underline disabled:opacity-50"
          >
            {isFetchingNextPage ? 'Lade ältere Nachrichten...' : 'Ältere Nachrichten laden'}
          </button>
        </div>
      )}
      {messages.length === 0 && !isFetchingNextPage && (
        <div className="text-center py-12 text-gray-400">
          <p className="text-sm">Noch keine Nachrichten.</p>
        </div>
      )}
      <div className="space-y-6">
        {Object.entries(grouped).map(([dateKey, msgs]) => (
          <div key={dateKey}>
            <div className="text-center mb-3">
              <span className="text-[10px] text-gray-400 bg-white px-3 py-1 rounded-full shadow-sm border border-gray-100">
                {formatFullDate(msgs[0]?.created_at ?? dateKey)}
              </span>
            </div>
            <div className="space-y-3">
              {msgs.map(msg => (
                <DmMessageBubble
                  key={msg.id}
                  message={msg}
                  participant={participantMap.get(msg.sender_id)}
                  isOwn={msg.sender_id === currentUserId}
                  conversationId={conversation.conversation_id}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      <div ref={messagesEndRef} />
    </div>
  )
}
